/**
 * ThemeProvider — React context for per-tenant theming.
 *
 * Behavior:
 * - Only active when `portal_management` feature flag is enabled for the tenant.
 * - Banks Architecture and all tenants without the flag: zero CSS overrides,
 *   index.css defaults remain unchanged (zero visual regression).
 * - planTEK / portal tenants: fetches theme from backend and injects CSS vars.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { DEFAULT_THEME, TenantTheme, injectCSSVariables } from '@/lib/theme';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useFeature } from '@/lib/features/FeatureProvider';
import { api } from '@/lib/api';

interface ThemeContextValue {
  theme: TenantTheme;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Read from tenant feature flags (canonical source) — NOT user.modules.
  // The two systems can drift, which previously caused silent 403s on the
  // GET /api/me/settings/theme call. See docs/patterns/BACKEND_PATTERNS.md
  // → "Feature Flag Gating".
  const hasPortalManagement = useFeature('portal_management');
  const [theme, setTheme] = useState<TenantTheme>(DEFAULT_THEME);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Only fetch + inject when portal_management flag is enabled.
    // This guarantees zero CSS regression for tenants without the flag.
    if (!user?.tenant_id || !hasPortalManagement) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    api.settings
      .getTheme()
      .then((data) => {
        if (!cancelled) {
          injectCSSVariables(data as TenantTheme);
          setTheme(data as TenantTheme);
        }
      })
      .catch(() => {
        // Silent fallback — CSS defaults from index.css remain in effect
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.tenant_id, hasPortalManagement]);

  return (
    <ThemeContext.Provider value={{ theme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
